import type { ActivityAnalytics, AdminUserSummary } from "@/lib/api";

import { ActivityChart } from "./activity-chart";
import { ActivityExplorer } from "./activity-explorer";

interface Props {
  analytics: ActivityAnalytics;
  users: AdminUserSummary[];
  actions: string[];
}

const SECTION_HEADING = "text-primary type-label";

export function ActivityDashboard({ analytics, users, actions }: Props) {
  const { summary, timeseries, breakdown } = analytics;

  const stats = [
    { label: "Events", value: summary.total_events },
    { label: "Logins", value: summary.total_logins },
    { label: "Active users", value: summary.active_users },
    { label: "Signups", value: summary.signups },
  ];

  return (
    <div className="flex flex-col gap-8">
      <section>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="border-border rounded-2xl border bg-white p-5">
              <p className="text-muted text-[13px]">{stat.label}</p>
              <p className="text-primary mt-1 text-[32px] leading-10 font-semibold tracking-[-0.32px]">
                {stat.value}
              </p>
            </div>
          ))}
        </div>
        <p className="text-muted mt-2 text-[13px]">Last {summary.days} days</p>
      </section>

      <section>
        <h2 className={`${SECTION_HEADING} mb-3`}>Activity over time</h2>
        <div className="border-border rounded-2xl border bg-white p-6">
          <ActivityChart data={timeseries} />
          <div className="text-muted mt-3 flex items-center justify-between text-[13px]">
            <span>{timeseries[0]?.day}</span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="bg-accent inline-block h-2 w-2 rounded-full" /> logins
              </span>
              <span className="flex items-center gap-1">
                <span className="bg-accent/20 inline-block h-2 w-2 rounded-full" /> all events
              </span>
            </span>
            <span>{timeseries[timeseries.length - 1]?.day}</span>
          </div>
        </div>
      </section>

      {breakdown.length > 0 && (
        <section>
          <h2 className={`${SECTION_HEADING} mb-3`}>By action</h2>
          <div className="border-border flex flex-col gap-2 rounded-2xl border bg-white p-6">
            {breakdown.map((item) => (
              <div key={item.action} className="flex items-center justify-between text-[15px]">
                <span className="text-primary font-medium">{item.action}</span>
                <span className="text-muted">{item.count}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className={`${SECTION_HEADING} mb-3`}>Recent activity</h2>
        <ActivityExplorer users={users} actions={actions} />
      </section>
    </div>
  );
}
